import React from 'react';
import { useTranslation } from 'react-i18next';
import gtmEvent from '../../../lib/gtmEvents';

import Heading from '../../Heading';

/**
 * Dealer purchase section component. Shows selected tyre, tyre availability and purchase button.
 *
 * @param {object} param0
 * @param {Dealer} param0.dealer
 * @param {DealerProduct} [param0.product]
 * @param {import('../../../../modules/buying-options-bar').BuyingOptionsData} [param0.buyingOptionsProduct]
 * @param {boolean} param0.loading
 * @param {number} [param0.headingLevel=3] Heading level for section headings
 * @returns
 */
function DealerPurchaseSection({ dealer, product, buyingOptionsProduct, loading, headingLevel = 3 }) {
  const { t } = useTranslation('dealers');

  // NTCONT-1733: Don't show this section at all if no product. But keep for future use.
  if(!product) {
    return null;
  }

  let purchaseHref = dealer.purchaseUrl || '';
  let purchaseLabel = t('Go to dealer website');
  let purchaseInfo = '';
  if (product) {
    purchaseHref = product.purchaseUrl;
    if (product.availability === 'OutOfStock') {
      purchaseLabel = t('Go to dealer website');
    }
    else {
      purchaseLabel = t('Buy from this dealer');
    }
    purchaseInfo = t('Product availability notice');
  }
  else if (buyingOptionsProduct) {
    purchaseInfo = t('Product availability not found notice');
    purchaseLabel = t('Go to dealer website');
  }

  if (loading) {
    purchaseInfo = '';
    purchaseLabel = t('Loading product information');
  }

  if (!purchaseHref) {
    return null;
  }

  const url = new URL(purchaseHref);

  // Add utm campaign parameters
  url.searchParams.set('utm_source', window.location.hostname);
  url.searchParams.set('utm_medium', 'referral');
  url.searchParams.set('utm_campaign', 'dealer_card');
  url.searchParams.set('utm_content', dealer.source_id || dealer.slug);
  if (buyingOptionsProduct?.selectedCode) {
    url.searchParams.set('utm_term', buyingOptionsProduct.selectedCode);
  }

  purchaseHref = url.toString();

  function handleClick() {
    gtmEvent({
      event: 'dealer_purchase_click',
      dealer: dealer.source_id || dealer.slug,
      productCode: product?.productCode || '',
    });
  }

  const productInfo = buyingOptionsProduct?.sizes?.find(item => item.code === buyingOptionsProduct.selectedCode)?.productInfo;
  const stockStatusMap = {
    InStock: {
      label: t('In stock'),
    },
    LimitedAvailability: {
      label: t('Limited availability'),
    },
    OutOfStock: {
      label: t('Out of stock'),
    },
  };
  const stockInfo = product && stockStatusMap[product.stockStatus] ? stockStatusMap[product.stockStatus] : false;

  console.log('DealerPurchaseSection render', { dealer, product, buyingOptionsProduct, loading });

  return (
    <div className="dealer-purchase-section">
      {buyingOptionsProduct && (
        <>
          <Heading level={headingLevel} className="dealer-purchase-section__title h6">{t('Your chosen tyre')}</Heading>

          <div className="dealer-purchase-tyre">
            {buyingOptionsProduct.image && (
              <div className="dealer-purchase-tyre__image">
                <img src={buyingOptionsProduct.image} alt="" />
              </div>
            )}
            <div className="dealer-purchase-tyre__content">
              <strong className="dealer-purchase-tyre__product-name">
                <a href={buyingOptionsProduct.productUrl}>
                  {buyingOptionsProduct.marketingName}
                </a>
              </strong>
              <p className="dealer-purchase-tyre__product-info">{productInfo} ({buyingOptionsProduct.selectedCode})</p>
            </div>
            <div className="dealer-purchase-tyre__details">
              {stockInfo && (
                <div className={'dealer-purchase-tyre__stock-status dealer-purchase-tyre__stock-status--' + product.stockStatus}>
                  <strong>{t('Availability')}:</strong>
                  <p><span className="icon icon--tyre"></span>{stockInfo.label}</p>
                </div>
              )}
            </div>
          </div>
        </>
      )}

      <a
        href={loading ? '#' : purchaseHref}
        className="button button--primary dealer-purchase-section__purchase-button"
        target="_blank"
        onClick={handleClick}
        data-loading={loading ? 'true' : 'false'}
      >
        {purchaseLabel}
      </a>
      {purchaseInfo && (
        <p className="dealer-purchase-section__purchase-info">{purchaseInfo}</p>
      )}
    </div>
  );
}

export default DealerPurchaseSection;
